import type { LicenseType } from '@/mocks/saLicensing';
import { licenseTypeLabels } from '@/mocks/saLicensing';

interface LicenseFiltersProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  activeTypeFilter: LicenseType | 'all';
  onTypeFilterChange: (v: LicenseType | 'all') => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (v: 'grid' | 'list') => void;
  typeCounts: Record<string, number>;
  onCompare: () => void;
  onNewLicense: () => void;
}

const allTypes: (LicenseType | 'all')[] = ['all', 'trial', 'free', 'monthly', 'annual', 'lifetime', 'enterprise'];

const typeBadgeStyles: Record<string, string> = {
  all: 'bg-foreground-950 text-background-50',
  trial: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  free: 'bg-background-200 text-foreground-600 border-background-300',
  monthly: 'bg-sky-50 text-sky-700 border-sky-200',
  annual: 'bg-amber-50 text-amber-700 border-amber-200',
  lifetime: 'bg-violet-50 text-violet-700 border-violet-200',
  enterprise: 'bg-foreground-950 text-background-50',
};

const activeStyles: Record<string, string> = {
  all: 'bg-foreground-950 text-background-50',
  trial: 'bg-emerald-500 text-background-50',
  free: 'bg-stone-600 text-background-50',
  monthly: 'bg-sky-500 text-background-50',
  annual: 'bg-amber-500 text-background-50',
  lifetime: 'bg-violet-500 text-background-50',
  enterprise: 'bg-foreground-950 text-background-50',
};

export default function LicenseFilters({
  searchTerm,
  onSearchChange,
  activeTypeFilter,
  onTypeFilterChange,
  viewMode,
  onViewModeChange,
  typeCounts,
  onCompare,
  onNewLicense,
}: LicenseFiltersProps) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar licencia por nombre, tipo o feature..."
            className="w-full pl-8 pr-8 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-[10px]"></i>
            </button>
          )}
        </div>

        {/* Type filters */}
        <div className="flex items-center gap-1 flex-wrap">
          {allTypes.map((type) => {
            const count = type === 'all'
              ? Object.values(typeCounts).reduce((a, b) => a + b, 0)
              : typeCounts[type] || 0;
            const isActive = activeTypeFilter === type;
            const label = type === 'all' ? 'Todas' : licenseTypeLabels[type];

            return (
              <button
                key={type}
                onClick={() => onTypeFilterChange(type)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? activeStyles[type]
                    : `bg-background-100 text-foreground-500 border border-background-200 hover:border-foreground-300 ${typeBadgeStyles[type]}`
                }`}
              >
                {label}
                <span className={`px-1 py-0.5 rounded-full text-[8px] font-bold ${
                  isActive ? 'bg-white/20 text-inherit' : 'bg-background-200 text-foreground-400'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* View toggle */}
          <div className="flex items-center bg-background-100 rounded-xl p-0.5 border border-background-200">
            <button
              onClick={() => onViewModeChange('grid')}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                viewMode === 'grid' ? 'bg-background-50 shadow-sm text-foreground-800' : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              <i className="ri-grid-line text-xs"></i>
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                viewMode === 'list' ? 'bg-background-50 shadow-sm text-foreground-800' : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              <i className="ri-list-check text-xs"></i>
            </button>
          </div>

          <button
            onClick={onCompare}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-accent-50 text-accent-700 text-xs font-semibold hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap border border-accent-200"
          >
            <i className="ri-scales-3-line text-xs"></i>
            <span className="hidden sm:inline">Comparar</span>
          </button>

          <button
            onClick={onNewLicense}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            <span className="hidden sm:inline">Nueva Licencia</span>
          </button>
        </div>
      </div>
    </div>
  );
}